import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") {
    return detail
      .replace(/^Value error[,:]?\s*/i, "")
      .replace(/^Assertion failed[,:]?\s*/i, "")
      .trim() || detail;
  }
  if (Array.isArray(detail)) {
    return detail
      .map((e) => {
        if (!e) return "";
        if (typeof e === "string") return e;
        let msg = typeof e.msg === "string" ? e.msg : "";
        msg = msg.replace(/^Value error[,:]?\s*/i, "").trim();
        // Prefer field-specific friendly password/phone messages
        const loc = Array.isArray(e.loc) ? e.loc.join(".") : "";
        if (/password/i.test(loc) || /password/i.test(msg)) {
          if (/at least 10/i.test(msg)) return "Password must be at least 10 characters.";
          if (/lowercase/i.test(msg)) return "Password must include at least one lowercase letter (a–z).";
          if (/uppercase/i.test(msg)) return "Password must include at least one uppercase letter (A–Z).";
          if (/number|digit/i.test(msg)) return "Password must include at least one number (0–9).";
        }
        if (/phone/i.test(loc) || /phone/i.test(msg)) {
          return "Enter a valid 10-digit Indian mobile number (starting with 6–9).";
        }
        return msg || JSON.stringify(e);
      })
      .filter(Boolean)
      .join(" ");
  }
  if (detail && typeof detail.msg === "string") {
    return detail.msg.replace(/^Value error[,:]?\s*/i, "").trim();
  }
  return String(detail);
}

/** Application display timezone for India-facing tenants. */
export const APP_TIME_ZONE = "Asia/Kolkata";

export function formatDate(dateString, options = {}) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    timeZone: options.timeZone || APP_TIME_ZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function formatDateTime(dateString, options = {}) {
  if (!dateString) return "-";
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    timeZone: options.timeZone || APP_TIME_ZONE,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

/** Date + time for expiry / validity displays. */
export function formatExpiryDateTime(dateString) {
  return formatDateTime(dateString, { timeZone: APP_TIME_ZONE });
}

/** Date-only expiry (avoids off-by-one calendar day confusion near midnight). */
export function formatExpiryDate(dateString) {
  return formatDate(dateString, { timeZone: APP_TIME_ZONE });
}

export function getStatusColor(status, isDark = false) {
  if (isDark) {
    const darkColors = {
      created: 'bg-zinc-800 text-zinc-300 border-zinc-700',
      manufactured: 'bg-brand-500/10 text-brand-400 border-brand-500/20',
      packed: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/20',
      shipped: 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
      delivered: 'bg-green-500/10 text-green-400 border-green-500/20',
      verified: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
    return darkColors[status?.toLowerCase()] || darkColors.created;
  }
  
  const colors = {
    created: 'bg-zinc-100 text-zinc-700 border-zinc-300',
    manufactured: 'bg-brand-50 text-brand-600 border-brand-200',
    packed: 'bg-yellow-50 text-yellow-600 border-yellow-200',
    shipped: 'bg-indigo-50 text-indigo-600 border-indigo-200',
    delivered: 'bg-green-50 text-green-600 border-green-200',
    verified: 'bg-emerald-50 text-emerald-600 border-emerald-200',
  };
  return colors[status?.toLowerCase()] || colors.created;
}

export const QR_STATUSES = [
  { value: 'created', label: 'Created' },
  { value: 'manufactured', label: 'Manufactured' },
  { value: 'packed', label: 'Packed' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'verified', label: 'Verified' },
];
